/**
 * Correlation-id source — file 05 §L2664, §L2704 invariant.
 *
 * `correlationId` is mandatory on every SdkError. Generated client-side as
 * a ULID on every fetch / publish, sent as `x-correlation-id` on REST and as
 * a NATS header. Servers MUST echo the same id back in error responses /
 * pg_notify payloads so a single id stitches: browser → REST → DB → indexer
 * → NATS → toast in Better Stack Logs.
 *
 * This file intentionally ships an inline ULID generator rather than pulling
 * the `ulid` package — the SDK targets both web and React Native and the
 * function is small enough to inline. Output is a 26-character Crockford
 * base-32 string identical to the published `ulid` package format.
 */

const CROCKFORD = '0123456789ABCDEFGHJKMNPQRSTVWXYZ' as const;
const TIME_LEN = 10;
const RAND_LEN = 16;

function getRandomBytes(n: number): Uint8Array {
  const out = new Uint8Array(n);
  const g: { crypto?: { getRandomValues?: (b: Uint8Array) => Uint8Array } } =
    globalThis as unknown as {
      crypto?: { getRandomValues?: (b: Uint8Array) => Uint8Array };
    };
  if (g.crypto?.getRandomValues) {
    g.crypto.getRandomValues(out);
    return out;
  }
  // Fallback for environments without WebCrypto. Math.random is not
  // cryptographically strong but correlation ids do not need to be — they
  // only need to be globally unique with overwhelming probability.
  for (let i = 0; i < n; i++) {
    out[i] = Math.floor(Math.random() * 256);
  }
  return out;
}

function encodeTime(now: number): string {
  let t = now;
  const buf = new Array<string>(TIME_LEN);
  for (let i = TIME_LEN - 1; i >= 0; i--) {
    const mod = t % 32;
    buf[i] = CROCKFORD[mod] ?? '0';
    t = (t - mod) / 32;
  }
  return buf.join('');
}

function encodeRandom(): string {
  const bytes = getRandomBytes(RAND_LEN);
  const buf = new Array<string>(RAND_LEN);
  for (let i = 0; i < RAND_LEN; i++) {
    const b = bytes[i] ?? 0;
    buf[i] = CROCKFORD[b % 32] ?? '0';
  }
  return buf.join('');
}

/** Produce a fresh ULID (26 chars, Crockford base-32). */
export function newCorrelationId(): string {
  return encodeTime(Date.now()) + encodeRandom();
}

/** Structural check — 26 chars, Crockford alphabet only. */
export function isCorrelationId(value: unknown): value is string {
  if (typeof value !== 'string') return false;
  if (value.length !== TIME_LEN + RAND_LEN) return false;
  for (let i = 0; i < value.length; i++) {
    if (!CROCKFORD.includes(value[i] as string)) return false;
  }
  return true;
}

/**
 * Pull the correlation id off a Headers / record object, accepting both
 * the canonical `x-correlation-id` casing and the upper-cased variant some
 * intermediaries emit. Returns `undefined` if absent.
 */
export function readCorrelationIdFromHeaders(
  headers: Headers | Record<string, string | string[] | undefined> | undefined,
): string | undefined {
  if (headers === undefined) return undefined;
  if (typeof Headers !== 'undefined' && headers instanceof Headers) {
    const v = headers.get('x-correlation-id');
    return v === null ? undefined : v;
  }
  const rec = headers as Record<string, string | string[] | undefined>;
  const v = rec['x-correlation-id'] ?? rec['X-Correlation-Id'] ?? rec['X-CORRELATION-ID'];
  if (v === undefined) return undefined;
  return Array.isArray(v) ? v[0] : v;
}

/**
 * Resolve the correlation id for an SdkError construction site. Prefer the
 * server-echoed header (so logs join across tiers); fall back to a fresh
 * client ULID. Never returns undefined — `correlationId` is mandatory.
 */
export function resolveCorrelationId(fromHeader: string | undefined | null): string {
  if (typeof fromHeader === 'string' && fromHeader.length > 0) return fromHeader;
  return newCorrelationId();
}
